<script lang="ts">
  import InboxWorkflowPreconfigurationBox from "@kontextwerk/control-web-frame/shared/modules/inbox-workflow-preconfiguration-box"
  import type { InboxWorkflowPreconfigurationProps } from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
  import {
      notifyInboxWorkflowPreconfigurationApplyResult,
      resolveInboxWorkflowPreconfigurationTargets,
  } from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { propertyCustomerRoleOptions } from "../../../../../../../../../../../../_shared/_logic/customer-roles"
  import {
    KEEP_PRECONFIGURATION_VALUE,
    isKeepPreconfigurationChoice,
    withKeepPreconfigurationOption,
    type PreconfigurationChoice,
  } from "../../../../../../../../../../../../_shared/_logic/preconfiguration-keep"
  import type {
    AssignRequesterCustomerDraft,
    AssignRequesterCustomerRole,
    AssignRequesterCustomerRow,
    RequesterCustomerCandidate,
  } from "../../contract"

  type AssignRequesterDecision = "create_new" | "use_existing"
  let {
    rows,
    selectedIds,
    draftFor,
    patchDraft,
    disabled = false,
  }: InboxWorkflowPreconfigurationProps<
    AssignRequesterCustomerRow,
    AssignRequesterCustomerDraft
  > = $props()

  // Decision and role start per row from matches and submitted data, so both keep by default.
  let decision = $state<PreconfigurationChoice<AssignRequesterDecision>>(
    KEEP_PRECONFIGURATION_VALUE,
  )
  let role = $state<PreconfigurationChoice<AssignRequesterCustomerRole>>(
    KEEP_PRECONFIGURATION_VALUE,
  )

  const targetResolution = $derived(
    resolveInboxWorkflowPreconfigurationTargets({
      rows,
      selectedIds,
      getId: (row) => row.actionCaseId,
      isEligible: (row) => !row.disabled,
    }),
  )

  const firstCustomerOption = (
    row: AssignRequesterCustomerRow,
  ): RequesterCustomerCandidate | null => row.customerOptions?.[0] ?? null

  const textValue = (...values: unknown[]): string => {
    for (const value of values) {
      if (typeof value === "string" && value.trim()) return value.trim()

      if (typeof value === "number" && Number.isFinite(value)) return String(value)
    }

    return ""
  }

  const patchFor = (
    row: AssignRequesterCustomerRow,
  ): Partial<AssignRequesterCustomerDraft> | null => {
    const draft = draftFor(row)
    const keptRole = isKeepPreconfigurationChoice(role) ? draft.role : role

    if (isKeepPreconfigurationChoice(decision)) {
      return isKeepPreconfigurationChoice(role) ? {} : { ...draft, role }
    }

    if (decision === "create_new") {
      const displayName = textValue(draft.displayName, row.submittedName)
      const email = draft.email
      const phone = draft.phone

      return {
        ...draft,
        decision: "create_new",
        customerId: null,
        displayName,
        email,
        phone,
        role: keptRole,
      }
    }

    const option = firstCustomerOption(row)

    if (!option?.customerId) return null

    return {
      ...draft,
      decision: "use_existing",
      customerId: option.customerId,
      role: option.role || keptRole,
      email: option.email || "",
      phone: option.phone || "",
    }
  }

  const apply = () => {
    let applied = 0
    let skipped = targetResolution.skippedRows.length
    for (const row of targetResolution.targetRows) {
      const patch = patchFor(row)

      if (!patch) {
        skipped += 1
        continue
      }

      if (Object.keys(patch).length) patchDraft(row, patch)

      applied += 1
    }
    notifyInboxWorkflowPreconfigurationApplyResult({
      total: targetResolution.targetRows.length + targetResolution.skippedRows.length,
      applied,
      skipped,
      reason:
        decision === "use_existing"
          ? "Für diese Zeilen gibt es keinen passenden bestehenden Ansprechpartner."
          : undefined,
    })
  }
</script>

<InboxWorkflowPreconfigurationBox
  selectedCount={selectedIds.length}
  targetCount={targetResolution.targetRows.length}
  {disabled}
  onApply={apply}
>
  <div class="preconfiguration-grid">
    <Input
      type="select"
      label="Als was speichern?"
      bind:value={decision}
      brightBackground={false}
      surfaceTone="dark"
      options={withKeepPreconfigurationOption([
        { value: "create_new", name: "Neu anlegen" },
        { value: "use_existing", name: "Vorhandenen zuordnen" },
      ])}
    />
    <Input
      type="select"
      label="Welche Rolle hat dieser Ansprechpartner?"
      bind:value={role}
      brightBackground={false}
      surfaceTone="dark"
      options={withKeepPreconfigurationOption(
        propertyCustomerRoleOptions.map((option) => ({
          value: option.value,
          name: option.name,
        })),
      )}
    />
  </div>
</InboxWorkflowPreconfigurationBox>

<style>
  .preconfiguration-grid {
    padding-top: 1rem;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, minmax(12rem, 1fr));
  }
</style>
